import clsx from 'clsx';
import { useMemo, useState } from 'react';

import { diffFieldLists, type DocumentTypeResponse, type FieldDiff } from '../api';
import { ConfirmDialog } from './ConfirmDialog';

interface VersionHistoryProps {
  versions: DocumentTypeResponse[];
  /**
   * When provided, a "Restaurar esta versión" action appears for every non-head
   * row. The editor passes this callback when it has wired the rollback mutation.
   */
  onRollback?: (version: string) => void;
  /** Disables the restore button while a mutation is in flight. */
  rollbackDisabled?: boolean;
}

function badgeFor(status: FieldDiff['status']): { label: string; className: string } {
  switch (status) {
    case 'added':
      return { label: 'Añadido', className: 'bg-emerald-100 text-emerald-800' };
    case 'removed':
      return { label: 'Eliminado', className: 'bg-rose-100 text-rose-800' };
    case 'modified':
      return { label: 'Modificado', className: 'bg-amber-100 text-amber-800' };
    case 'unchanged':
      return { label: 'Sin cambios', className: 'bg-slate-100 text-slate-600' };
  }
}

/**
 * Passive "Historial / restaurar versión" panel. Shows a vertical timeline of
 * versions plus a side-by-side diff. Non-head rows offer a "Restaurar esta
 * versión" action that applies the old version as a new forward version via
 * the rollback endpoint. There is no branch/draft concept any more.
 */
export function VersionHistory({
  versions,
  onRollback,
  rollbackDisabled = false,
}: VersionHistoryProps): JSX.Element {
  const sorted = useMemo(() => versions.slice(), [versions]);

  // Head is index 0 (backend returns descending).
  const headVersion = sorted[0]?.version;

  const [selectedVersion, setSelectedVersion] = useState<string | null>(
    headVersion ?? null,
  );
  const [confirmRestoreVersion, setConfirmRestoreVersion] = useState<string | null>(null);

  const currentIndex = sorted.findIndex((v) => v.version === selectedVersion);
  const current = currentIndex >= 0 ? sorted[currentIndex] : undefined;
  // "Previous" in time = the entry with the next-higher index (list is desc).
  const previous =
    currentIndex >= 0 && currentIndex + 1 < sorted.length ? sorted[currentIndex + 1] : undefined;

  const diffs = useMemo<FieldDiff[]>(() => {
    if (current === undefined || previous === undefined) {
      return [];
    }
    return diffFieldLists(previous.fields, current.fields).filter((d) => d.status !== 'unchanged');
  }, [current, previous]);

  if (versions.length === 0) {
    return (
      <div className="card p-6 text-sm text-slate-500">
        Este tipo de documento aún no tiene versiones guardadas.
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ol className="space-y-2 lg:col-span-1">
          {sorted.map((version, idx) => {
            const isSelected = version.version === selectedVersion;
            const isHead = idx === 0;
            return (
              <li key={version.version} className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => setSelectedVersion(version.version)}
                  className={clsx(
                    'flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                    isSelected
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                  )}
                >
                  <span className="font-mono font-semibold">v{version.version}</span>
                  <div className="flex items-center gap-2">
                    {isHead ? (
                      <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500">
                        actual
                      </span>
                    ) : null}
                    <span className="text-xs text-slate-500">{version.fields.length} campos</span>
                  </div>
                </button>
                {!isHead && onRollback !== undefined ? (
                  <button
                    type="button"
                    className="self-end text-xs font-medium text-primary underline-offset-2 hover:underline disabled:opacity-50"
                    onClick={() => setConfirmRestoreVersion(version.version)}
                    disabled={rollbackDisabled}
                    data-testid={`rollback-to-${version.version}`}
                  >
                    Restaurar esta versión
                  </button>
                ) : null}
              </li>
            );
          })}
        </ol>

        <div className="lg:col-span-2">
          {current === undefined ? (
            <div className="card p-6 text-sm text-slate-500">
              Selecciona una versión para ver sus cambios.
            </div>
          ) : previous === undefined ? (
            <div className="card p-6 text-sm text-slate-500">
              Esta es la primera versión guardada — no hay versión anterior con la que comparar.
            </div>
          ) : (
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-slate-900">
                Cambios respecto a v{previous.version}
              </h3>
              {diffs.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">
                  No hay diferencias en los campos entre estas dos versiones.
                </p>
              ) : (
                <ul className="mt-3 space-y-1.5">
                  {diffs.map((diff) => {
                    const badge = badgeFor(diff.status);
                    return (
                      <li
                        key={`${diff.name}-${diff.status}`}
                        className="flex items-start justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 text-sm"
                      >
                        <div>
                          <span className="font-mono text-xs">{diff.name}</span>
                          {diff.detail !== undefined ? (
                            <div className="mt-0.5 text-xs text-slate-500">{diff.detail}</div>
                          ) : null}
                        </div>
                        <span
                          className={clsx(
                            'inline-flex rounded-full px-2 py-0.5 text-xs font-semibold',
                            badge.className,
                          )}
                        >
                          {badge.label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {onRollback !== undefined ? (
        <ConfirmDialog
          open={confirmRestoreVersion !== null}
          tone="danger"
          title={`¿Restaurar v${confirmRestoreVersion ?? ''}?`}
          description={
            <span>
              Esto aplica la versión <strong className="font-mono">v{confirmRestoreVersion ?? ''}</strong> como
              una nueva versión hacia adelante. Los campos y umbrales de esa versión reemplazarán
              la configuración actual. La acción no elimina el historial existente.
            </span>
          }
          confirmLabel="Restaurar versión"
          busy={rollbackDisabled}
          onConfirm={() => {
            if (confirmRestoreVersion !== null) {
              onRollback(confirmRestoreVersion);
              setConfirmRestoreVersion(null);
            }
          }}
          onCancel={() => setConfirmRestoreVersion(null)}
        />
      ) : null}
    </>
  );
}
